import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import DownloadIcon from '@mui/icons-material/Download';
import Button from '@mui/material/Button';
import ReactGA from 'react-ga4';
import logo from '../assets/images/finaculator1.png';  
import addWatermark from './watermark';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
interface LumpSumPDFGeneratorProps {
  principal: number | string;
  rate: number;
  period: number;
  finalAmount: number | null;
  totalInterest: number | null;
  isCalculated: boolean;
}

const LumpSumPDFGenerator: React.FC<LumpSumPDFGeneratorProps> = ({
  principal,
  rate,
  period,
  finalAmount,
  totalInterest,
  isCalculated
}) => {

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [hasChanged, setHasChanged] = useState(false);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  useEffect(() => {
    setHasChanged(true);
  }, [principal, rate, period]);

  const generatePDF = () => {


    if (!principal || !rate || !period || finalAmount === null || totalInterest === null) {
      setSnackbarMessage("Please ensure all values are calculated and not null before downloading the PDF.");
      setSnackbarOpen(true);
      return;
    }
    ReactGA.event({ 'category': 'Lumpsum PDF ', 'action': 'Lumpsum PDF Button', 'label': 'Lumpsum PDF' });

    const doc = new jsPDF();
    const watermarkWidth = 100;  
    const watermarkHeight = 100;  
  
    const title = 'Lump Sum Investment Summary';
    const titleFontSize = 14;
    const bodyFontSize = 10;

    const verticalSpacing = 15;
    const titleWidth = doc.getStringUnitWidth(title) * titleFontSize / doc.internal.scaleFactor;
    const pageWidth = doc.internal.pageSize.getWidth();
    const titleX = (pageWidth - titleWidth) / 2;

    doc.setFontSize(titleFontSize);
    doc.text(title, titleX, verticalSpacing);
    
    const principalAmount = typeof principal === 'number' && !isNaN(principal) 
      ? principal 
      : typeof principal === 'string' 
      ? parseFloat(principal.replace(/,/g, '')) 
      : 0;

    console.log('Principal Amount for PDF:', principalAmount);
    
    const inputDataRows = [
      ['Principal Amount', principalAmount.toFixed(2)],
      ['Annual Interest Rate', `${rate}%`],
      ['Time Period', `${period} years`],
      ['Final Amount', `${finalAmount !== null ? finalAmount.toFixed(2) : 'N/A'}`],
      ['Total Interest Earned', `${totalInterest !== null ? totalInterest.toFixed(2) : 'N/A'}`],
    ];

    autoTable(doc, {
      head: [['Input Details', 'Values']],
      body: inputDataRows,
      startY: verticalSpacing + 20,
      headStyles: { fillColor: [22, 160, 133] },
      theme: 'grid',
      styles: { overflow: 'linebreak', cellWidth: 'auto', fontSize: bodyFontSize },
    });

    const yearWiseData = getYearWiseData(principalAmount, rate, period);
    
    autoTable(doc, {
      head: [['Year', 'Final Amount', 'Total Interest Earned']],
      body: yearWiseData,
      startY: doc.autoTable.previous.finalY + 10,
      headStyles: { fillColor: [22, 160, 133] },
      theme: 'grid',
      styles: { overflow: 'linebreak', cellWidth: 'auto', fontSize: bodyFontSize,halign:'center' },
    });
    addWatermark(doc, logo, watermarkWidth, watermarkHeight);
    const img = new Image();
    img.src = logo;
    img.onload = () => {
      const now = new Date();
      const padZero = (num: string | number) => ("0" + num).slice(-2);
      const formattedDate = `${now.getFullYear()}-${padZero(now.getMonth() + 1)}-${padZero(now.getDate())}`;
      const formattedTime = `${padZero(now.getHours())}-${padZero(now.getMinutes())}-${padZero(now.getSeconds())}`;
      const timestamp = `${formattedDate}_${formattedTime}`;
      const fileName = `finaculator_lumpsum_calculator_${timestamp}.pdf`;
     doc.save(fileName);
  };
};

  const getYearWiseData = (principal: number, interestRate: number, years: number) => {
    const yearWiseData = [];
    const rateValue = interestRate / 100;

    for (let year = 1; year <= years; year++) {
      const finalAmountForYear = principal * Math.pow(1 + rateValue, year);
      const totalInterestEarned = finalAmountForYear - principal;

      yearWiseData.push([
        year.toString(),
        `${finalAmountForYear.toFixed(2)}`,
        `${totalInterestEarned.toFixed(2)}`,
      ]);
    }

    return yearWiseData;
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
      <Button
        variant="contained"
        color="success"
        onClick={generatePDF}
        style={{ marginTop: -70 }} 
        startIcon={<DownloadIcon />}
        disabled={! hasChanged}
      >
        Download PDF
      </Button>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default LumpSumPDFGenerator;
